import React, { useState, useMemo } from 'react';
import {
 HelpCircle,
 BookOpen,
 MessageSquare,
 FileText,
 Phone,
 Mail,
 Users,
 Settings,
 CreditCard,
 Calendar,
} from 'lucide-react';
import { Input, Button } from './index';

const ICON_MAP = {
 BookOpen,
 Calendar,
 CreditCard,
 Settings,
 FileText,
 MessageSquare,
 Mail,
 Phone,
 Users,
};

const getIcon = (name) => ICON_MAP[name] || HelpCircle;

const DEFAULT_HERO = { title: 'Vendor Support', subtitle: 'Everything you need to succeed as a vendor on VendaList' };
const DEFAULT_TABS = [
 { id: 'getting-started', label: 'Getting Started', icon: 'BookOpen' },
 { id: 'bookings', label: 'Managing Bookings', icon: 'Calendar' },
 { id: 'payments', label: 'Payments & Earnings', icon: 'CreditCard' },
 { id: 'profile', label: 'Profile & Services', icon: 'Settings' },
 { id: 'resources', label: 'Resources', icon: 'FileText' },
];
const DEFAULT_FAQS = {};
const DEFAULT_CONTACT = [];
const DEFAULT_QUICK_LINKS = [];

/**
 * Presentational component for vendor support page content.
 * Receives hero, tabs, faqs, contact_options, quick_links from settings.
 */
const VendorSupportContent = ({
 hero = DEFAULT_HERO,
 tabs = DEFAULT_TABS,
 faqs = DEFAULT_FAQS,
 contact_options = DEFAULT_CONTACT,
 quick_links = DEFAULT_QUICK_LINKS,
 searchPlaceholder = 'Search vendor support...',
 showSearch = true,
}) => {
 const [activeTab, setActiveTab] = useState(tabs[0]?.id || 'getting-started');
 const [searchQuery, setSearchQuery] = useState('');

 const filteredFaqs = useMemo(() => {
 const list = faqs[activeTab] || [];
 if (!searchQuery.trim()) return list;
 const q = searchQuery.toLowerCase();
 return list.filter(
 (faq) =>
 (faq.question || '').toLowerCase().includes(q) ||
 (faq.answer || '').toLowerCase().includes(q)
 );
 }, [faqs, activeTab, searchQuery]);

 return (
 <div className="min-h-screen bg-canvas">
 {/* Hero Section */}
 {/* <div className="bg-gradient-to-br from-black to-gray-900 text-white py-12 sm:py-16">
 <div className="page-container px-4 sm:px-6">
 <div className="max-w-4xl mx-auto text-center">
 <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-surface/10 backdrop-blur-sm rounded-full mb-4 sm:mb-6">
 <HelpCircle className="w-6 h-6 sm:w-8 sm:h-8" />
 </div>
 <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
 {hero?.title || 'Vendor Support'}
 </h1>
 <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-4 sm:mb-6">
 {hero?.subtitle || 'Everything you need to succeed as a vendor on VendaList'}
 </p>

 {showSearch && (
 <div className="max-w-2xl mx-auto px-2">
 <div className="relative">
 <HelpCircle className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
 <Input
 type="text"
 placeholder={searchPlaceholder}
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 className="pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-surface/10 border-white/20 text-white placeholder-gray-400 focus:bg-surface/20"
 />
 </div>
 </div>
 )}
 </div>
 </div>
 </div> */}

 {/* Main Content */}
 <div className="p-6">
 {/* <div className="max-w-7xl mx-auto"> */}
 {/* Quick Links */}
 {Array.isArray(quick_links) && quick_links.length > 0 && (
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
 {quick_links.map((link, index) => {
 const IconComponent = getIcon(link.icon);
 return (
 <a
 key={index}
 href={link.href}
 className="bg-surface rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 hover:shadow-md transition-all text-center group"
 >
 <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-black rounded-lg mb-2 sm:mb-3 group-hover:bg-gray-800 transition-colors">
 <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
 </div>
 <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-1">{link.title}</h3>
 <p className="text-xs text-gray-600 hidden sm:block">{link.description}</p>
 </a>
 );
 })}
 </div>
 )}

 {/* Tabs Navigation */}
 {Array.isArray(tabs) && tabs.length > 0 && (
 <div className="bg-surface rounded-xl shadow-sm border border-gray-200 mb-6 sm:mb-8">
 <div className="border-b border-gray-200">
 <nav className="flex overflow-x-auto -mb-px px-2 sm:px-4 md:px-6 scrollbar-hide">
 {tabs.map((tab) => {
 const IconComponent = getIcon(tab.icon);
 return (
 <button
 key={tab.id}
 onClick={() => setActiveTab(tab.id)}
 className={`relative py-3 sm:py-4 px-2 sm:px-3 md:px-6 font-medium text-xs sm:text-sm md:text-base transition-all duration-300 whitespace-nowrap border-b-2 flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ${ activeTab === tab.id ? 'text-black border-black' : 'text-gray-600 border-transparent hover:text-black hover:border-gray-300' }`}
 >
 <IconComponent className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 flex-shrink-0" />
 <span className="hidden xs:inline sm:hidden md:inline">{tab.label}</span>
 <span className="xs:hidden sm:inline md:hidden">
 {tab.label?.split(' ')[0] || tab.label}
 </span>
 </button>
 );
 })}
 </nav>
 </div>

 {/* Tab Content */}
 <div className="p-3 sm:p-4 md:p-6 lg:p-8">
 <div className="space-y-4 sm:space-y-6">
 <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
 Frequently Asked Questions
 </h2>
 <div className="space-y-3 sm:space-y-4">
 {filteredFaqs.length > 0 ? (
 filteredFaqs.map((faq, index) => (
 <div
 key={index}
 className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-6 hover:border-gray-300 transition-colors"
 >
 <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2">
 {faq.question}
 </h3>
 <p className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed">
 {faq.answer}
 </p>
 </div>
 ))
 ) : (
 <p className="text-gray-500">No FAQs found for this section.</p>
 )}
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Contact Support Section */}
 {/* {Array.isArray(contact_options) && contact_options.length > 0 && (
 <div className="bg-gradient-to-br from-gray-900 to-neutral-900 text-white rounded-xl p-4 sm:p-6 md:p-8">
 <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 md:mb-6">Need More Help?</h2>
 <p className="text-xs sm:text-sm md:text-base text-gray-300 mb-4 sm:mb-6 md:mb-8">
 Our vendor support team is here to help you succeed
 </p>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
 {contact_options
 .filter((opt) => opt.available !== false)
 .map((option, index) => {
 const IconComponent = getIcon(option.icon);
 return (
 <div
 key={index}
 className="bg-surface/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 md:p-6 hover:bg-surface/20 transition-colors"
 >
 <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
 <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 flex-shrink-0" />
 <h3 className="text-sm sm:text-base md:text-lg font-semibold">{option.title}</h3>
 </div>
 <p className="text-xs sm:text-sm text-gray-300 mb-3 sm:mb-4 break-words">
 {option.description}
 </p>
 <Button
 variant="outline"
 size="sm"
 className="w-full bg-surface text-black hover:bg-gray-100 text-xs sm:text-sm"
 >
 {option.action}
 </Button>
 </div>
 );
 })}
 </div>
 </div>
 )} */}
 {/* </div> */}
 </div>
 </div>
 );
};

export default VendorSupportContent;
